import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CartPage, CtHead, CtPay, BtnTeal, Sk } from '@quizzach/styles';
import { Reveal } from '@quizzach/motion';
import { useCart, usePay } from '@quizzach/api-hooks';
import {
	AlertMessage,
	CartEmpty,
	CartError,
	CartNotAuthenticated,
	Navigation,
	PaymentOptions,
	SummaryTable,
} from '@quizzach/components';
import { ApiFailedReason, EnumQuizType, RoutePath } from '@quizzach/interfaces';
import { formatCartPrice } from '@quizzach/utils';
import { useAuth, useCartContext } from '@quizzach/contexts';

export const CartSummary = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	const { source } = location.state || {};
	const { isAuthenticated, loading: authLoading } = useAuth();
	const [termsConsent, setTermsConsent] = useState(false);
	const [legalConsent, setLegalConsent] = useState(false);
	const [showConsentWarning, setShowConsentWarning] = useState(false);
	const [apiCallFailed, setApiCallFailed] = useState(false);
	const [apiFailReason, setApiFailReason] = useState<ApiFailedReason>(
		ApiFailedReason.addToCart
	);

	const {
		cart,
		error,
		loading: cartLoading,
		deleteInProgress,
		retrieveCart,
		deleteCartItem,
	} = useCart();
	const { refreshCartCount } = useCartContext();
	const { handleFakePay } = usePay();

	// keep the nav badge in sync with deletions made on this page
	const deleteCartItemAndSync = async (uuid: string) => {
		await deleteCartItem(uuid);
		refreshCartCount();
	};
	const [selectedProvider, setSelectedProvider] = useState<string>('');

	useEffect(() => {
		if (isAuthenticated) {
			retrieveCart();
		}
	}, [isAuthenticated]);

	const payDisabled = !termsConsent || !legalConsent || !selectedProvider;

	const getFakePayFinishRoute = () => {
		switch (source) {
			case EnumQuizType.quick:
				return RoutePath.QuickEnd;
			case EnumQuizType.generator:
				return RoutePath.GeneratorEnd;
			default:
				return RoutePath.OrderFinish;
		}
	};

	// Corvus takes over the whole payment from here, so the handoff must stay a
	// full-page replace, never an SPA navigation; fakepay finishes in-app.
	const handlePay = async () => {
		if (payDisabled) {
			setShowConsentWarning(true);
			return;
		}
		setShowConsentWarning(false);

		if (selectedProvider === 'fakepay') {
			const success = await handleFakePay();
			if (success) {
				setApiCallFailed(false);
				navigate(getFakePayFinishRoute(), {
					state: { source: source ?? EnumQuizType.none },
				});
			} else {
				setApiFailReason(ApiFailedReason.default);
				setApiCallFailed(true);
			}
			return;
		}

		if (!cart?.order_number) return;
		window.location.replace(
			`/payment/cart/${cart.order_number}/corvus?method=${selectedProvider}`
		);
	};

	// cartLoading starts true and only resolves once retrieveCart runs, which
	// requires an authenticated user - so the auth check must come first.
	if (authLoading) {
		return (
			<CartPage>
				<Navigation />
			</CartPage>
		);
	}
	if (!isAuthenticated) return <CartNotAuthenticated />;
	if (cartLoading) {
		return (
			<CartPage>
				<Navigation />
				<div aria-hidden='true'>
					<CtHead>
						<div>
							<Sk style={{ height: 20, width: 140, marginBottom: 16 }} />
							<Sk style={{ height: 'clamp(44px, 6.4vw, 92px)', width: 320 }} />
						</div>
					</CtHead>
					<div style={{ padding: '0 var(--pad)' }}>
						{Array.from({ length: 2 }).map((_, index) => (
							<Sk key={index} style={{ height: 72, marginBottom: 14 }} />
						))}
					</div>
				</div>
			</CartPage>
		);
	}
	if (error && !cartLoading) return <CartError />;
	if (!error && !cartLoading && cart?.cart_items?.length === 0)
		return <CartEmpty />;

	const itemCount = cart?.cart_items?.length ?? 0;

	return (
		<CartPage>
			<Navigation />

			<CtHead>
				<div>
					<span className='hand'>{t('cart.redesign.hand')}</span>
					{/* homepage-hero treatment: the title rises out of an overflow
					    clip on load (styles live on CtHead) */}
					<h1>
						<span className='row'>
							<span>{t('common.checkout.title')}</span>
						</span>
					</h1>
				</div>
				<span className='cnt'>
					{t('cart.redesign.itemCount', { count: itemCount })}
				</span>
			</CtHead>

			<Reveal>
				<SummaryTable
					cart={cart}
					error={error}
					loading={cartLoading}
					deleteCartItem={deleteCartItemAndSync}
					deleteInProgress={deleteInProgress}
				/>
			</Reveal>

			<Reveal delay={1}>
				<CtPay>
					<div className='pt'>{t('cart.redesign.payTitle')}</div>
					<PaymentOptions
						legalConsent={legalConsent}
						setLegalConsent={setLegalConsent}
						selectedProvider={selectedProvider}
						setSelectedProvider={setSelectedProvider}
						termsConsent={termsConsent}
						setTermsConsent={setTermsConsent}
					/>

					{showConsentWarning && (
						<AlertMessage
							code={'termsConsent'}
							type='warning'
							style={{ margin: '0 0 20px' }}
						/>
					)}
					{apiCallFailed && (
						<AlertMessage
							code={apiFailReason}
							type='error'
							style={{ margin: '0 0 20px' }}
						/>
					)}

					<BtnTeal
						type='button'
						className={`pay-btn${payDisabled ? ' dis-look' : ''}`}
						style={{ opacity: payDisabled ? 0.45 : 1 }}
						onClick={handlePay}
					>
						<span>
							{t('common.checkout.next')}
							{cart?.total_price ? (
								<>
									{` · ${formatCartPrice(cart.total_price)} `}
									<span className='cur'>EUR</span>
								</>
							) : (
								''
							)}
						</span>
					</BtnTeal>
				</CtPay>
			</Reveal>
		</CartPage>
	);
};
